/**
 * ManageAttestations.js
 *
 * WHAT: Admin view for managing attestation records across active and completed campaigns.
 * WHY:  Compliance administrators need to monitor attestation progress, reassign attesters,
 *       and correct attestation records without being a direct attester themselves. This
 *       component provides campaign-level visibility into all attestation records with
 *       filtering, search, bulk reassignment, and individual record editing.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { WideModal } from './common';

/**
 * WHAT: Admin attestation management dashboard with record-level editing and bulk reassignment.
 * WHY:  When attesters leave the organization or records need correction, admins use this
 *       component to reassign attestations to new reviewers and ensure campaign completion.
 */
const ManageAttestations = () => {
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [attestationRecords, setAttestationRecords] = useState([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [recordsError, setRecordsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDecision, setFilterDecision] = useState('all');
  const [filterAttesterType, setFilterAttesterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [editFormData, setEditFormData] = useState({
    new_attester_id: '',
    reason: ''
  });
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignRecords, setReassignRecords] = useState([]);
  const [selectedReassignRecords, setSelectedReassignRecords] = useState([]);
  const [newAttesterId, setNewAttesterId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);
  const [currentAttesterEmail, setCurrentAttesterEmail] = useState('');
  const [availableAttesters, setAvailableAttesters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [resultModal, setResultModal] = useState({ show: false, title: '', message: '', isError: false });

  useEffect(() => {
    loadAttestationCampaigns();
    loadEnterpriseUsers();
  }, []);

  useEffect(() => {
    console.log('Enterprise users state updated:', enterpriseUsers.length, enterpriseUsers);
  }, [enterpriseUsers]);

  const loadEnterpriseUsers = async () => {
    try {
      const response = await fetch(API_CONFIG.getEnterpriseUsersEndpoint(''));
      const data = await response.json();

      console.log('Enterprise users response:', data);
      if (data.success) {
        console.log('Loaded enterprise users:', data.users.length);
        setEnterpriseUsers(data.users);
      } else {
        console.error('Failed to load enterprise users:', data.error);
      }
    } catch (err) {
      console.error('Error loading enterprise users:', err.message);
    }
  };

  const loadAttestationCampaigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();

      if (data.success) {
        // Filter campaigns that are in ATTESTATION or completed status
        const attestationCampaigns = data.campaigns.filter(
          campaign => campaign.status === 'ATTESTATION' || campaign.status === 'completed'
        );
        setCampaigns(attestationCampaigns);
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAttestationRecords = async (campaign) => {
    setIsLoadingRecords(true);
    setRecordsError(null);
    try {
      const params = new URLSearchParams({
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.campaign_name
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/records/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setAttestationRecords(data.records || []);
      } else {
        setRecordsError(data.error || 'Failed to load attestation records');
      }
    } catch (err) {
      setRecordsError('Error loading attestation records: ' + err.message);
    } finally {
      setIsLoadingRecords(false);
    }
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    loadAttestationRecords(campaign);
  };

  const handleBackToList = () => {
    setSelectedCampaign(null);
    setAttestationRecords([]);
    setRecordsError(null);
    setSearchTerm('');
    setFilterDecision('all');
    setFilterAttesterType('all');
    setFilterStatus('all');
    setSelectedRecords([]);
    setSelectAll(false);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const filteredRecords = getFilteredRecords();
      // Only select pending records (not completed)
      const pendingRecords = filteredRecords.filter(r => r.attestation_status !== 'Completed');
      setSelectedRecords(pendingRecords.map(r => r.attestation_id));
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (attestationId) => {
    setSelectedRecords(prev => {
      if (prev.includes(attestationId)) {
        return prev.filter(id => id !== attestationId);
      } else {
        return [...prev, attestationId];
      }
    });
  };

  const handleOpenReassignModal = () => {
    // Get unique attesters with pending records
    const attestersMap = {};
    attestationRecords.forEach(record => {
      if (record.attestation_status !== 'Completed' && record.manager_email) {
        if (!attestersMap[record.manager_email]) {
          attestersMap[record.manager_email] = {
            email: record.manager_email,
            name: record.manager_name,
            pendingCount: 0
          };
        }
        attestersMap[record.manager_email].pendingCount++;
      }
    });
    setAvailableAttesters(Object.values(attestersMap));
    setCurrentAttesterEmail('');
    setReassignRecords([]);
    setNewAttesterId('');
    setReassignReason('');
    setShowReassignModal(true);
  };

  const handleCurrentAttesterChange = (email) => {
    setCurrentAttesterEmail(email);
    setNewAttesterId('');
    // Filter records for the selected attester that are pending
    const records = attestationRecords.filter(
      r => r.manager_email === email && r.attestation_status !== 'Completed'
    );
    setReassignRecords(records);
    // Auto-select all records by default
    setSelectedReassignRecords(records.map(r => r.attestation_id));
  };

  const handleCloseReassignModal = () => {
    setShowReassignModal(false);
    setReassignRecords([]);
    setSelectedReassignRecords([]);
    setNewAttesterId('');
    setReassignReason('');
    setCurrentAttesterEmail('');
    setAvailableAttesters([]);
  };

  const handleReassignAttester = async () => {
    if (!newAttesterId) {
      setResultModal({ show: true, title: 'Validation Error', message: 'Please select a new attester.', isError: true });
      return;
    }

    if (selectedReassignRecords.length === 0) {
      setResultModal({ show: true, title: 'Validation Error', message: 'No records selected for reassignment.', isError: true });
      return;
    }

    setIsReassigning(true);

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process only selected records
      const recordsToReassign = reassignRecords.filter(r => selectedReassignRecords.includes(r.attestation_id));
      for (const record of recordsToReassign) {
        try {
          const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/update-attester/'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              campaign_id: selectedCampaign.campaign_id,
              campaign_name: selectedCampaign.campaign_name,
              attestation_id: record.attestation_id,
              new_attester_id: newAttesterId,
              reason: reassignReason
            })
          });

          const data = await response.json();

          if (data.success) {
            successCount++;
          } else {
            errorCount++;
            // Backend guard messages already lead with the username
            // ("XX user is skipped as self-attestation is not allowed") —
            // don't prefix those a second time.
            const errMsg = data.error || 'Unknown error';
            errors.push(errMsg.startsWith(record.db_username) ? errMsg : `${record.db_username}: ${errMsg}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`${record.db_username}: ${err.message}`);
        }
      }

      setIsReassigning(false);

      if (successCount > 0) {
        const selectedUser = enterpriseUsers.find(u => u.user_id === newAttesterId);
        const newAttesterName = selectedUser ? selectedUser.display_name : newAttesterId;

        let message = `Successfully reassigned ${successCount} record(s) to ${newAttesterName}.`;
        if (errorCount > 0) {
          message += `\n\n${errorCount} record(s) failed:\n${errors.join('\n')}`;
        }
        setResultModal({ show: true, title: 'Reassignment Complete', message, isError: errorCount > 0 });

        // Reload attestation records
        loadAttestationRecords(selectedCampaign);
        // Clear selection
        setSelectedRecords([]);
        setSelectAll(false);
        // Close reassign modal
        handleCloseReassignModal();
      } else {
        setResultModal({
          show: true,
          title: 'Reassignment Failed',
          message: `All reassignments failed:\n${errors.join('\n')}`,
          isError: true
        });
      }
    } catch (err) {
      setIsReassigning(false);
      setResultModal({ show: true, title: 'Reassignment Error', message: err.message, isError: true });
    }
  };

  const handleBulkReminder = async () => {
    if (selectedRecords.length === 0) {
      alert('Please select at least one record to send reminder');
      return;
    }

    if (!window.confirm(`Send reminder emails for ${selectedRecords.length} record(s)?`)) {
      return;
    }

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/send-reminders/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_id: selectedCampaign.campaign_id,
          campaign_name: selectedCampaign.campaign_name,
          attestation_ids: selectedRecords
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Success! Reminder emails sent to ${data.emails_sent} attester(s). Updated ${data.records_updated} record(s).`);
        // Reload attestation records to show updated reminder counts
        loadAttestationRecords(selectedCampaign);
        // Clear selection
        setSelectedRecords([]);
        setSelectAll(false);
      } else {
        alert('Error sending reminders: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error sending reminders: ' + err.message);
    }
  };

  const handleEditRecord = (record) => {
    console.log('Edit button clicked for record:', record);
    console.log('Available enterprise users:', enterpriseUsers.length);
    setEditingRecord(record);
    setEditFormData({
      new_attester_id: record.manager_id || '',
      reason: ''
    });
    setShowEditModal(true);
  };

  const handleSaveAttesterChange = async (e) => {
    e.preventDefault();

    if (!editFormData.new_attester_id) {
      alert('Please select a new attester');
      return;
    }

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/update-attester/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_id: selectedCampaign.campaign_id,
          campaign_name: selectedCampaign.campaign_name,
          attestation_id: editingRecord.attestation_id,
          new_attester_id: editFormData.new_attester_id,
          reason: editFormData.reason
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Attester updated successfully');
        setShowEditModal(false);
        setEditingRecord(null);
        // Reload attestation records
        loadAttestationRecords(selectedCampaign);
      } else {
        alert('Error updating attester: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error updating attester: ' + err.message);
    }
  };

  const handleSingleReminder = async (record) => {
    if (!window.confirm(`Send reminder email to ${record.manager_name} (${record.manager_email})?`)) {
      return;
    }

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/send-reminders/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_id: selectedCampaign.campaign_id,
          campaign_name: selectedCampaign.campaign_name,
          attestation_ids: [record.attestation_id]
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Success! Reminder email sent to ${record.manager_name}`);
        // Reload attestation records to show updated reminder count
        loadAttestationRecords(selectedCampaign);
      } else {
        alert('Error sending reminder: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error sending reminder: ' + err.message);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterDecision('all');
    setFilterAttesterType('all');
    setFilterStatus('all');
  };

  const getFilteredRecords = () => {
    return attestationRecords.filter(record => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        record.database_name?.toLowerCase().includes(searchLower) ||
        record.db_username?.toLowerCase().includes(searchLower) ||
        record.user_name?.toLowerCase().includes(searchLower) ||
        record.user_id?.toLowerCase().includes(searchLower) ||
        record.application_name?.toLowerCase().includes(searchLower) ||
        record.manager_name?.toLowerCase().includes(searchLower) ||
        record.manager_email?.toLowerCase().includes(searchLower);

      // Decision filter
      const matchesDecision = filterDecision === 'all' ||
        (filterDecision === 'Delegated' && record.is_delegated) ||
        (filterDecision !== 'Delegated' && record.attestation_decision === filterDecision);

      // Attester type filter
      const matchesAttesterType = filterAttesterType === 'all' ||
        record.attester_type === filterAttesterType;

      // Status filter
      const matchesStatus = filterStatus === 'all' ||
        record.attestation_status === filterStatus;

      return matchesSearch && matchesDecision && matchesAttesterType && matchesStatus;
    });
  };

  // Pagination helpers
  const getPaginatedRecords = () => {
    const filteredRecords = getFilteredRecords();
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredRecords = getFilteredRecords();
    return Math.ceil(filteredRecords.length / recordsPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDecision, filterAttesterType, filterStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({
        campaign_id: selectedCampaign.campaign_id,
        campaign_name: selectedCampaign.campaign_name,
        format: 'csv'
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/records/export/?${params.toString()}`));

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedCampaign.campaign_name}_Attestation_Records.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting CSV: ' + err.message);
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams({
        campaign_id: selectedCampaign.campaign_id,
        campaign_name: selectedCampaign.campaign_name,
        format: 'pdf'
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attestation/records/export/?${params.toString()}`));

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedCampaign.campaign_name}_Attestation_Records.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting PDF: ' + err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading attestation campaigns...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedCampaign) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <button
              className="btn btn-outline-secondary mb-4"
              onClick={handleBackToList}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Campaigns
            </button>

            <div className="card mb-3">
              <div className="card-header bg-primary text-white py-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div className="d-flex align-items-center me-3">
                    <h6 className="mb-0 me-3">
                      <i className="fas fa-clipboard-check me-2"></i>
                      {selectedCampaign.campaign_name}
                    </h6>
                    <span className="badge bg-warning">{selectedCampaign.status}</span>
                  </div>
                  <div className="d-flex align-items-center small flex-wrap gap-3">
                    <span><span style={{opacity: 0.85}}>Compliance:</span> <strong>{selectedCampaign.compliance_framework}</strong></span>
                    <span><span style={{opacity: 0.85}}>Database Type:</span> <strong>{selectedCampaign.database_type}</strong></span>
                    <span><span style={{opacity: 0.85}}>Created:</span> <strong>{formatDate(selectedCampaign.created_date)}</strong></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Attestation Records Section */}
            <div className="card">
              <div className="card-header bg-secondary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 me-3">
                    <i className="fas fa-list me-2"></i>
                    Attestation Records
                  </h5>
                  <div className="d-flex gap-2 ms-auto">
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={handleOpenReassignModal}
                      disabled={attestationRecords.length === 0 || selectedCampaign?.status === 'COMPLETED'}
                      title="Reassign Attester"
                    >
                      <i className="fas fa-user-edit me-1"></i>
                      Reassign Attester
                    </button>
                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-light"
                        onClick={handleExportCSV}
                        disabled={attestationRecords.length === 0}
                        title="Export to CSV"
                      >
                        <i className="fas fa-file-csv me-1"></i>
                        CSV
                      </button>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={handleExportPDF}
                        disabled={attestationRecords.length === 0}
                        title="Export to PDF"
                      >
                        <i className="fas fa-file-pdf me-1"></i>
                        PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Readonly warning for completed campaigns */}
                {selectedCampaign && selectedCampaign.status === 'COMPLETED' && (
                  <div className="alert alert-success mb-3">
                    <i className="fas fa-check-circle me-2"></i>
                    <strong>Campaign Completed!</strong> All attestations for this campaign have been completed.
                    This campaign is now read-only and cannot be modified.
                  </div>
                )}

                {isLoadingRecords ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading attestation records...</p>
                  </div>
                ) : recordsError ? (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {recordsError}
                  </div>
                ) : attestationRecords.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    No attestation records found for this campaign.
                  </div>
                ) : (
                  <>
                    {/* Search and Filter Section */}
                    <div className="card mb-3">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-4">
                            <label className="form-label fw-bold">
                              <i className="fas fa-search me-2"></i>Search
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by database, user, employee, application, or attester..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-bold">
                              <i className="fas fa-gavel me-2"></i>Decision
                            </label>
                            <select
                              className="form-select"
                              value={filterDecision}
                              onChange={(e) => setFilterDecision(e.target.value)}
                            >
                              <option value="all">All Decisions</option>
                              <option value="Retained">Retained</option>
                              <option value="Modify">Modify</option>
                              <option value="Removed">Removed</option>
                              <option value="Delegated">Delegated</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-bold">
                              <i className="fas fa-user-tag me-2"></i>Attester Type
                            </label>
                            <select
                              className="form-select"
                              value={filterAttesterType}
                              onChange={(e) => setFilterAttesterType(e.target.value)}
                            >
                              <option value="all">All Types</option>
                              <option value="Account Owner">Account Owner</option>
                              <option value="Application Owner">Application Owner</option>
                              <option value="Database Owner">Database Owner</option>
                              <option value="User Manager">User Manager</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-bold">
                              <i className="fas fa-flag me-2"></i>Status
                            </label>
                            <select
                              className="form-select"
                              value={filterStatus}
                              onChange={(e) => setFilterStatus(e.target.value)}
                            >
                              <option value="all">All Status</option>
                              <option value="Pending">Pending</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-bold">&nbsp;</label>
                            <button
                              className="btn btn-outline-secondary w-100"
                              onClick={handleClearFilters}
                            >
                              <i className="fas fa-eraser me-2"></i>Clear Filters
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="d-flex flex-wrap gap-2 mb-3" style={{fontSize: '0.95rem'}}>
                      <span className="badge bg-primary d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-users"></i> Total: <strong style={{fontSize: '1.1rem'}}>{getFilteredRecords().length}/{attestationRecords.length}</strong>
                      </span>
                      <span className="badge bg-warning text-dark d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-clock"></i> Pending: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.filter(r => !r.attestation_decision || r.attestation_decision === 'Pending').length}</strong>
                      </span>
                      <span className="badge bg-success d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-check-circle"></i> Retained: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.filter(r => r.attestation_decision === 'Retained').length}</strong>
                      </span>
                      <span className="badge bg-info d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-edit"></i> Modified: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.filter(r => r.attestation_decision === 'Modify').length}</strong>
                      </span>
                      <span className="badge bg-danger d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-user-times"></i> Removed: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.filter(r => r.attestation_decision === 'Removed' || r.attestation_decision === 'Rejected').length}</strong>
                      </span>
                      <span className="badge d-flex align-items-center gap-2 px-3 py-2 rounded" style={{background: '#805ad5'}}>
                        <i className="fas fa-share"></i> Delegated: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.filter(r => r.is_delegated).length}</strong>
                      </span>
                      <span className="badge bg-secondary d-flex align-items-center gap-2 px-3 py-2 rounded">
                        <i className="fas fa-percentage"></i> Done: <strong style={{fontSize: '1.1rem'}}>{attestationRecords.length > 0
                          ? Math.round((attestationRecords.filter(r => r.attestation_decision && r.attestation_decision !== 'Pending').length / attestationRecords.length) * 100)
                          : 0}%</strong>
                      </span>
                    </div>

                    {/* Bulk Actions */}
                    {selectedRecords.length > 0 && (
                      <div className="alert alert-info mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="fas fa-check-square me-2"></i>
                            <strong>{selectedRecords.length}</strong> record(s) selected
                          </div>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={handleBulkReminder}
                            >
                              <i className="fas fa-envelope me-2"></i>Send Reminder
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {setSelectedRecords([]); setSelectAll(false);}}
                            >
                              <i className="fas fa-times me-2"></i>Clear Selection
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-striped table-hover table-sm" style={{fontSize: '0.8rem'}}>
                        <thead className="table-dark">
                          <tr>
                            <th style={{ width: '30px' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectAll}
                                onChange={handleSelectAll}
                              />
                            </th>
                            <th style={{whiteSpace: 'nowrap'}}>Database</th>
                            <th style={{whiteSpace: 'nowrap'}}>DB User</th>
                            <th style={{whiteSpace: 'nowrap'}}>Employee</th>
                            <th style={{whiteSpace: 'nowrap'}}>App</th>
                            <th style={{minWidth: '100px'}}>Attester</th>
                            <th style={{whiteSpace: 'nowrap'}}>Type</th>
                            <th style={{whiteSpace: 'nowrap'}}>Decision</th>
                            <th style={{whiteSpace: 'nowrap'}}>Status</th>
                            <th style={{whiteSpace: 'nowrap'}}># Reminders</th>
                            <th style={{whiteSpace: 'nowrap'}}>Last Reminded</th>
                            <th style={{whiteSpace: 'nowrap'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedRecords().map((record) => (
                            <tr key={record.attestation_id}>
                              <td>
                                {record.attestation_status !== 'Completed' ? (
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedRecords.includes(record.attestation_id)}
                                    onChange={() => handleSelectRecord(record.attestation_id)}
                                  />
                                ) : (
                                  <i className="fas fa-check-circle text-success" title="Attestation Completed"></i>
                                )}
                              </td>
                              <td style={{ maxWidth: '90px' }}>
                                <div className="text-truncate" title={record.database_name}>
                                  {record.database_name}
                                </div>
                                <small className="text-muted">{record.database_type}</small>
                              </td>
                              <td style={{ maxWidth: '80px' }}>
                                <div className="text-truncate" title={record.db_username}>
                                  {record.db_username}
                                </div>
                                <small className="text-muted">{record.account_status}</small>
                              </td>
                              <td style={{ maxWidth: '80px' }}>
                                <div className="text-truncate" title={record.user_name}>
                                  {record.user_name}
                                </div>
                                <small className="text-muted">{record.user_id}</small>
                              </td>
                              <td style={{ maxWidth: '80px' }}>
                                <div className="text-truncate" title={record.application_name}>
                                  {record.application_name || 'N/A'}
                                </div>
                              </td>
                              <td style={{ maxWidth: '100px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                <div style={{ fontSize: '0.8rem' }} title={record.manager_name}>
                                  {record.manager_name}
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{record.manager_email}</small>
                              </td>
                              <td>
                                <span className={`badge ${
                                  record.attester_type === 'Account Owner' ? 'bg-primary' :
                                  record.attester_type === 'Application Owner' ? 'bg-info' :
                                  record.attester_type === 'Database Owner' ? 'bg-success' :
                                  record.attester_type === 'Substitute' ? 'bg-warning' :
                                  'bg-secondary'
                                }`}>
                                  {record.attester_type}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  record.attestation_decision === 'Approved' ? 'bg-success' :
                                  record.attestation_decision === 'Removed' ? 'bg-danger' :
                                  'bg-secondary'
                                }`}>
                                  {record.attestation_decision}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  record.attestation_status === 'Completed' ? 'bg-success' :
                                  record.attestation_status === 'In Progress' ? 'bg-info' :
                                  'bg-secondary'
                                }`}>
                                  {record.attestation_status}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className={`badge ${
                                  (record.reminder_count || 0) === 0 ? 'bg-secondary' :
                                  (record.reminder_count || 0) <= 2 ? 'bg-info' :
                                  'bg-warning text-dark'
                                }`}>
                                  {record.reminder_count || 0}
                                </span>
                              </td>
                              <td>
                                {record.last_reminder_date ? (
                                  <small className="text-muted">
                                    {formatDate(record.last_reminder_date)}
                                  </small>
                                ) : (
                                  <small className="text-muted">Never</small>
                                )}
                              </td>
                              <td>
                                {record.attestation_status !== 'Completed' ? (
                                  <div className="btn-group" role="group">
                                    <button
                                      className="btn btn-sm btn-outline-warning"
                                      onClick={() => handleSingleReminder(record)}
                                      title="Send Reminder"
                                    >
                                      <i className="fas fa-envelope"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-muted" title="No reminder needed - attestation complete">
                                    <i className="fas fa-check text-success"></i>
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    {getFilteredRecords().length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          <span className="me-2">Show</span>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={recordsPerPage}
                            onChange={handleRecordsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="ms-2">records per page</span>
                        </div>
                        <div>
                          <small className="text-muted">
                            Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, getFilteredRecords().length)} of {getFilteredRecords().length} records
                          </small>
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                              >
                                <i className="fas fa-angle-double-left"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                <i className="fas fa-angle-left"></i>
                              </button>
                            </li>
                            {/* Page numbers */}
                            {(() => {
                              const totalPages = getTotalPages();
                              const pages = [];
                              let startPage = Math.max(1, currentPage - 2);
                              let endPage = Math.min(totalPages, currentPage + 2);

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className="page-item">
                                    <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                                  </li>
                                );
                                if (startPage > 2) {
                                  pages.push(
                                    <li key="ellipsis1" className="page-item disabled">
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                                  </li>
                                );
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key="ellipsis2" className="page-item disabled">
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                                pages.push(
                                  <li key={totalPages} className="page-item">
                                    <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                                  </li>
                                );
                              }

                              return pages;
                            })()}
                            <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === getTotalPages()}
                              >
                                <i className="fas fa-angle-right"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(getTotalPages())}
                                disabled={currentPage === getTotalPages()}
                              >
                                <i className="fas fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reassign Attester Modal */}
        <WideModal
          show={showReassignModal}
          onClose={handleCloseReassignModal}
          title="Reassign Attester"
          headerIcon="fa-user-edit"
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseReassignModal}
                disabled={isReassigning}
              >
                <i className="fas fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReassignAttester}
                disabled={isReassigning || !newAttesterId || !currentAttesterEmail || !reassignReason || selectedReassignRecords.length === 0}
              >
                {isReassigning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Reassigning...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Reassign Attester
                  </>
                )}
              </button>
            </>
          }
        >
                  <div className="alert alert-info mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    Select an attester to replace, then choose which pending records to reassign. All records are selected by default — uncheck individual records to keep them with the current attester.
                  </div>

                  {/* Current Attester Selection */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user me-2"></i>
                      Current Attester to Replace *
                    </label>
                    <select
                      className="form-select"
                      value={currentAttesterEmail}
                      onChange={(e) => handleCurrentAttesterChange(e.target.value)}
                      disabled={isReassigning}
                    >
                      <option value="">-- Select Current Attester --</option>
                      {availableAttesters.map(attester => (
                        <option key={attester.email} value={attester.email}>
                          {attester.name} ({attester.email}) - {attester.pendingCount} pending records
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Pending Records for Selected Attester */}
                  {currentAttesterEmail && reassignRecords.length > 0 && (
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">
                        <i className="fas fa-list me-2"></i>
                        Pending Records ({selectedReassignRecords.length} of {reassignRecords.length} selected)
                      </h6>
                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-sm table-bordered table-striped">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th style={{ width: '40px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedReassignRecords.length === reassignRecords.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedReassignRecords(reassignRecords.map(r => r.attestation_id));
                                    } else {
                                      setSelectedReassignRecords([]);
                                    }
                                  }}
                                  disabled={isReassigning}
                                />
                              </th>
                              <th>Database</th>
                              <th>DB Username</th>
                              <th>Employee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reassignRecords.map((record, index) => (
                              <tr key={index} style={{ backgroundColor: selectedReassignRecords.includes(record.attestation_id) ? '' : '#f8f9fa' }}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedReassignRecords.includes(record.attestation_id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedReassignRecords(prev => [...prev, record.attestation_id]);
                                      } else {
                                        setSelectedReassignRecords(prev => prev.filter(id => id !== record.attestation_id));
                                      }
                                    }}
                                    disabled={isReassigning}
                                  />
                                </td>
                                <td>{record.database_name}</td>
                                <td>{record.db_username}</td>
                                <td>{record.user_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* New Attester Selection */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-plus me-2"></i>
                      New Attester *
                    </label>
                    <select
                      className="form-select"
                      value={newAttesterId}
                      onChange={(e) => setNewAttesterId(e.target.value)}
                      disabled={isReassigning}
                    >
                      <option value="">-- Select New Attester --</option>
                      {enterpriseUsers
                        .filter(user => {
                          // Exclude the currently selected attester
                          return user.email?.toLowerCase() !== currentAttesterEmail?.toLowerCase();
                        })
                        .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
                        .map(user => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.display_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {newAttesterId && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted">
                          <strong>Selected:</strong> {enterpriseUsers.find(u => u.user_id === newAttesterId)?.email}
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Reason for Reassignment */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-comment me-2"></i>
                      Reason for Reassignment *
                    </label>
                    <select
                      className="form-select"
                      value={reassignReason}
                      onChange={(e) => setReassignReason(e.target.value)}
                      disabled={isReassigning}
                    >
                      <option value="">-- Select Reason --</option>
                      <option value="Out of Office">Out of Office</option>
                      <option value="End of Employment">End of Employment</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
        </WideModal>

        {/* Result Modal for reassignment success/error feedback */}
        <WideModal
          show={resultModal.show}
          onClose={() => setResultModal({ show: false, title: '', message: '', isError: false })}
          title={resultModal.title}
          headerIcon={resultModal.isError ? 'fa-exclamation-triangle' : 'fa-check-circle'}
          headerClass={resultModal.isError ? 'bg-danger text-white' : 'bg-success text-white'}
          maxWidth="700px"
          footer={
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setResultModal({ show: false, title: '', message: '', isError: false })}
            >
              Close
            </button>
          }
        >
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
            {resultModal.message}
          </div>
        </WideModal>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2 className="text-primary mb-4">
            <i className="fas fa-clipboard-check me-2"></i>
            Manage Attestations
          </h2>

          {campaigns.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No campaigns are currently in the attestation phase.
            </div>
          ) : (
            <>
              <div className="alert alert-info mb-4">
                <i className="fas fa-info-circle me-2"></i>
                <strong>{campaigns.length}</strong> campaign(s) are currently in the attestation phase. Select a campaign to manage attestations.
              </div>

              <div className="row">
                {campaigns.map((campaign) => (
                  <div key={campaign.campaign_id} className="col-md-6 col-lg-4 mb-4">
                    <div className="card h-100 shadow-sm hover-shadow" style={{ cursor: 'pointer' }}>
                      <div className="card-body" onClick={() => handleCampaignSelect(campaign)}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="card-title text-primary mb-0">
                            {campaign.campaign_name}
                          </h5>
                          <span className={`badge ${campaign.status === 'COMPLETED' ? 'bg-success' : 'bg-warning'}`}>
                            {campaign.status}
                          </span>
                        </div>

                        <p className="card-text text-muted small mb-3">
                          {campaign.campaign_description}
                        </p>

                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="fas fa-shield-alt me-2"></i>
                            <strong>Compliance:</strong> {campaign.compliance_framework}
                          </small>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="fas fa-database me-2"></i>
                            <strong>Database Type:</strong> {campaign.database_type}
                          </small>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="fas fa-calendar me-2"></i>
                            <strong>Created:</strong> {formatDate(campaign.created_date)}
                          </small>
                        </div>

                        <div className="mb-3">
                          <small className="text-muted">
                            <i className="fas fa-user me-2"></i>
                            <strong>Created By:</strong> {campaign.created_by}
                          </small>
                        </div>

                        <div className="d-grid">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleCampaignSelect(campaign)}
                          >
                            <i className="fas fa-clipboard-check me-2"></i>
                            Manage Attestations
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default ManageAttestations;
